import { mixins, tokens } from 'src/design-system';

/**
 * Additional Vendor Charges — Part 2 tabbed grids styles (ISS-291749 / C2).
 */
const styles = () => ({
  container: {
    ...mixins.layout.flexColumn(),
    gap: `${tokens.spacing.sm}px`,
    width: '100%',
  },

  /** Active tab uses the mock's orange accent (text + ink bar) instead of antd's default blue. */
  tabs: {
    '& .ant-tabs-nav .ant-tabs-tab-active': {
      color: tokens.colors.tabActive,
    },
    '& .ant-tabs-nav .ant-tabs-tab:hover': {
      color: tokens.colors.tabActive,
    },
    '& .ant-tabs-ink-bar': {
      backgroundColor: tokens.colors.tabActive,
    },
  },

  /**
   * Flat, per the mock — no card/panel around a section. Just the title row, a small gap, then the
   * grid. Sections are separated by the bottom margin; the last one sits flush above the footer.
   */
  section: {
    ...mixins.layout.flexColumn(),
    gap: `${tokens.spacing.xs}px`,
    marginBottom: tokens.spacing.lg,
    '&:last-child': {
      marginBottom: tokens.spacing.none,
    },
  },

  sectionHeader: {
    ...mixins.layout.flexRow(undefined, 'baseline'),
    gap: `${tokens.spacing.xs}px`,
  },

  /** Charge name — 12px semibold #333, matching the mock (was an oversized 16px heading). */
  sectionTitle: {
    ...mixins.typography.small,
    color: tokens.colors.textSecondary,
    fontWeight: tokens.fontWeight.semibold,
  },

  /** The "(BL Level)" / "(Container Level)" applicability tag beside the name. */
  sectionMeta: {
    ...mixins.typography.caption,
    color: tokens.colors.textTertiary,
  },

  /** "BL-wise" / "Container-wise" descriptor line beneath the charge name (per the mock). */
  sectionSubtitle: {
    ...mixins.typography.caption,
    color: tokens.colors.textTertiary,
  },

  /**
   * The column header carries the periwinkle tint from the design mock (`bgTableHeader`), so the grid
   * reads as a structured table rather than a flat list.
   */
  /**
   * Compact grid matching the mock: 10px header on the #E3F2FD tint, 12px body, tight 6/8 cell padding.
   */
  table: {
    width: '100%',
    // Keep the column headers on one line so cells read cleanly once the grid can scroll.
    '& .ant-table-thead > tr > th': {
      backgroundColor: tokens.colors.bgTableHeader,
      color: tokens.colors.textSecondary,
      fontSize: tokens.fontSize.caption,
      fontWeight: tokens.fontWeight.semibold,
      padding: `${tokens.spacing.xs}px ${tokens.spacing.sm}px`,
      borderBottom: 'none',
      whiteSpace: 'nowrap',
    },
    '& .ant-table-tbody > tr > td': {
      color: tokens.colors.textSecondary,
      fontSize: tokens.fontSize.small,
      padding: `${tokens.spacing.xs}px ${tokens.spacing.sm}px`,
      whiteSpace: 'nowrap',
    },
    // Editable cells (money/text inputs, date pickers) need a floor width or they collapse to a sliver
    // in the wider grids — e.g. Basic Value / CGST / SGST / IGST in Third-Party.
    '& .ant-table-tbody .ant-input': {
      minWidth: tokens.spacing.xxl + tokens.spacing.md, // 64
    },
    '& .ant-table-tbody .ant-calendar-picker': {
      minWidth: tokens.spacing.xxl * 2, // 96
    },
  },

  /**
   * Sits directly beneath the grid: the container's flex gap gives the small gap above, and an explicit
   * bottom pad gives the breathing room below so the buttons never jam against the modal edge.
   */
  footer: {
    ...mixins.layout.modalFooter,
    gap: `${tokens.spacing.sm}px`,
    padding: `${tokens.spacing.none}px 0 ${tokens.spacing.md}px`,
  },

  /** "Edit charges" sits at the far left of the footer; the primary actions stay right-aligned. */
  editChargesBtn: {
    marginRight: 'auto',
  },

  message: {
    ...mixins.typography.small,
    color: tokens.colors.textTertiary,
  },

  /**
   * Static width overrides for the currency and third-party-vendor-code `Select`s in
   * `AdditionalChargeGridColumns.tsx` (ISS-292248 review fix, MINOR 8) — that file has no component of
   * its own to `injectSheet`, so these classes are defined here (a `.styles.ts` alongside it, same as
   * every other static value in this folder) and threaded down through `AdditionalChargeGridExtras`,
   * rather than an inline `style={{ minWidth }}` object in the renderer.
   */
  currencySelect: {
    minWidth: tokens.spacing.xxl + tokens.spacing.xl + tokens.spacing.sm + tokens.spacing.xxs, // 90
  },
  thirdPartyVendorSelect: {
    minWidth: tokens.spacing.xxl * 2 + tokens.spacing.xl + tokens.spacing.ms, // 140
  },

  /**
   * The per-currency exchange-rate footer (ISS-292248), sitting directly beneath the tabs and above the
   * Save/Submit action row.
   */
  exchangeRatesFooter: {
    ...mixins.typography.caption,
    color: tokens.colors.textTertiary,
    marginTop: tokens.spacing.md,
  },

  errorMessage: {
    ...mixins.typography.small,
    color: tokens.colors.error,
  },

  spinner: {
    ...mixins.layout.flexCenter,
    padding: tokens.spacing.lg,
  },
});

export default styles;
